import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack';
import styled from 'styled-components';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { useInView } from 'react-intersection-observer';

import { useComponentSize, useScrollSpy } from 'Util/Hooks';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

import { Flex, Box, Text, SvgIcon } from '../Base';

const ContentOuterWrapper = styled((props) => (
  <Flex
    width={1}
    height={'100%'}
    flexDirection={'column'}
    alignItems={'center'}
    style={{ position: 'relative' }}
    flex="1 0 auto"
    maxHeight={'100vh'}
    {...props}
  />
))`
  & > div {
    ::-webkit-scrollbar {
      display: none;
    }
  }
`;

const DocumentWrapper = React.forwardRef((props, ref) => (
  <Box
    width={1}
    height={'100%'}
    style={{
      position: 'absolute',
      overflow: 'auto',
      top: 0,
      left: 0,
    }}
    {...props}
    ref={ref}
  />
));
DocumentWrapper.displayName = 'DocumentWrapper';

const PageWrapper = styled((props) => (
  <Flex width={1} px={2} justifyContent={'space-between'} {...props} />
))`
  position: absolute;
  z-index: 1;
  top: ${({ theme }) => `${theme.space[4]}px`};
`;

const BlockWrapper = styled((props) => (
  <Flex justifyContent={'center'} alignItems={'center'} py={2} px={4} {...props} />
))`
  background-color: rgba(20, 41, 62, 0.6);
  border-radius: 8px;
`;
const pdfWrapperRef = React.createRef(null);
const pdfDocumentRef = React.createRef(null);

const PageObserver = ({ onLoadPdf }) => {
  const paperSize = useComponentSize(pdfWrapperRef);
  const pages = pdfDocumentRef?.current?.pages;
  const pageNumber = useScrollSpy({
    sectionElementRefs: pages,
    offsetPx: -paperSize?.top - 1,
    containerElement: pdfWrapperRef.current,
  });
  return (
    <PageWrapper>
      <BlockWrapper px={4}>
        <Text color={'white'}>{`${pageNumber + 1} ${
          pages?.length ? `of ${pages.length}` : ' '
        }`}</Text>
      </BlockWrapper>
      <BlockWrapper onClick={onLoadPdf} style={{ cursor: 'pointer' }}>
        <SvgIcon icon={SvgIcon.ICON_LIST.DOWNLOAD} color={'white'} />
      </BlockWrapper>
    </PageWrapper>
  );
};
const defaultConsoleFunction = window.console.log;
const overrideConsoleFunction = (...args) => {
  const firstArgs = args?.[0];
  if (typeof firstArgs === 'string' && firstArgs.includes('Warning: Unimplemented')) return;
  defaultConsoleFunction(...args);
};

const DocumentPage = ({ page, containerProps, onRenderPageSuccess, ...props }) => {
  const paperSize = useComponentSize(pdfWrapperRef);
  const [ref, inView] = useInView({ threshold: 0.1 });

  return (
    <Box {...containerProps} minHeight={paperSize?.height} minWidth={paperSize?.width} ref={ref}>
      {inView && (
        <Page
          pageNumber={page}
          renderMode={'canvas'}
          width={paperSize?.width}
          onRenderSuccess={
            typeof onRenderPageSuccess === 'function' ? onRenderPageSuccess(page) : () => {}
          }
          {...props}
        />
      )}
    </Box>
  );
};

const DocumentPages = ({ onRenderPageSuccess, containerProps, ...props }) => {
  const pages = pdfDocumentRef?.current?.pages?.length;
  const [allowPanning, setAllowPanning] = useState(false);
  return (
    <TransformWrapper
      wheel={{ wheelEnabled: false }}
      pan={{ disabled: !allowPanning }}
      onZoomChange={({ scale }) => {
        if (scale <= 1) {
          setAllowPanning(false);
        } else {
          setAllowPanning(true);
        }
      }}
      doubleClick={{ mode: 'reset' }}
    >
      <TransformComponent>
        {Array.from({ length: pages }).map((e, pageIndex) => {
          const pageNumber = pageIndex + 1;
          return (
            <DocumentPage
              key={pageNumber}
              page={pageNumber}
              onRenderPageSuccess={onRenderPageSuccess}
              containerProps={{
                ...containerProps,
                pb: pageNumber !== pages ? 3 : 0,
              }}
              {...props}
            />
          );
        })}
      </TransformComponent>
    </TransformWrapper>
  );
};

const PdfViewer = ({
  fileUrl,
  onLoadPdf,
  containerProps,
  documentContainerProps,
  documentProps,
  pageProps,
  pageContainerProps,
  children,
  ...props
}) => {
  const [allPages, setAllPages] = useState(0);
  const [isRenderSuccess, setIsRenderSuccess] = useState(false);

  useEffect(() => {
    window.console.log = overrideConsoleFunction;
    return () => {
      window.console.log = defaultConsoleFunction;
    };
  }, []);

  const src = useMemo(
    () => ({
      url: fileUrl,
    }),
    [fileUrl]
  );
  const onDocumentLoadSuccess = ({ numPages }) => {
    const onLoadSuccess = documentProps?.onLoadSuccess;
    typeof onLoadSuccess === 'function' && onLoadSuccess(numPages);
    setAllPages(numPages);
  };

  const { loading, ...otherDocumentProps } = documentProps || {};
  return (
    <ContentOuterWrapper {...containerProps}>
      {pdfWrapperRef.current && allPages !== 0 && <PageObserver onLoadPdf={onLoadPdf} />}
      <DocumentWrapper
        ref={pdfWrapperRef}
        {...documentContainerProps}
        pb={isRenderSuccess ? documentContainerProps?.pb : 0}
      >
        <Document
          ref={pdfDocumentRef}
          {...otherDocumentProps}
          file={src}
          renderMode={'canvas'}
          onLoadSuccess={onDocumentLoadSuccess}
        >
          <DocumentPages
            containerProps={pageContainerProps}
            onRenderPageSuccess={(page) => page === allPages && setIsRenderSuccess(true)}
            {...pageProps}
          />
          {children && isRenderSuccess && React.cloneElement(children)}
        </Document>
        {allPages === 0 && (loading ?? null)}
      </DocumentWrapper>
    </ContentOuterWrapper>
  );
};

PdfViewer.propTypes = {
  fileUrl: PropTypes.string,
  onLoadPdf: PropTypes.func,
  containerProps: PropTypes.object,
  documentContainerProps: PropTypes.object,
  documentProps: PropTypes.object,
  pageProps: PropTypes.object,
  pageContainerProps: PropTypes.object,
};

PdfViewer.defaultProps = {
  onLoadPdf: () => {},
};

const MemoPdfViewer = React.memo(
  PdfViewer,
  (prevProps, nextProps) => prevProps.fileUrl === nextProps.fileUrl
);

export default MemoPdfViewer;
